import {api} from "@/shared/api";
import {Integrations} from "../integrations";
import {FormUtil} from "@/shared/forms/formUtil";
import {htmlEscape} from "@/shared/utils/string";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Input from "@/shared/utils/input";
import {BmdAbsenceTypeDetails, DayBasedWorkingTimeTypeDetails, FieldError, Integration} from "@/shared/timr";
import {IntegrationType} from "@/shared/timrTypes";
import type {BmdAbsenceTypeConfig, BmdConfig, BmdDivNlzIdConfig} from "./bmd";

const template = `
	<div id="bmdConfigDialog">
		<form id="bmdConfigForm" class="form-horizontal">
			<div class="form-group">
				<div class="col-xs-12">
					<div class="flex-row justify-left mb-40 form-group">
						<div class="flex-column flex-vertical-center mr-15">
							<label class="text-nowrap mr-20 pt-8" for="bmdCompanyId">${messages["integrations.bmd.companyId"]}</label>
						</div>
						<div class="flex-column">
							<input type="text" class="form-control width-100" id="bmdCompanyId" name="companyId"/>
						</div>
					</div>
					<label class="checkbox-inline display-block mb-15" for="bmdTimeAccountAbsenceExportEnabled">
						<input type="checkbox" id="bmdTimeAccountAbsenceExportEnabled">
						<span class="ml-5">${messages["integrations.bmd.absenceExportEnabled"]}</span>
					</label>
					<div id="bmdAbsenceExportSection" class="mb-25" style="display: none;">
						<label class="checkbox-inline display-block mb-15" for="bmdTimeAccountBalanceExportEnabled">
							<input type="checkbox" id="bmdTimeAccountBalanceExportEnabled">
							<span class="ml-5">${messages["integrations.bmd.timeAccountBalanceExportEnabled"]}</span>
						</label>
						<div data-field="absenceTypes" class="mb-20 panel panel-bordered p-15 pb-10 form-group" style="max-height: 300px; overflow-y: auto">
						<h6 class="panel-title">${messages["integrations.bmd.absencesToExport"]}</h6>
						<div class="panel-body">
							<div class="flex-row">
								<div class="flex-column-30">${messages["integrations.bmd.workingTimeType"]}</div>
								<div class="flex-column-10 mr-15">${messages["integrations.bmd.export"]}</div>
								<div class="flex-column-60">${messages["integrations.bmd.absenceType"]}</div>
							</div>
							<div id="workingTimeTypeContainer">
								<!-- Working time type row -->
							</div>
							</div>
						</div>
					</div>
					<label class="checkbox-inline display-block mb-15" for="bmdTimeAccountPayrollExportEnabled">
						<input type="checkbox" id="bmdTimeAccountPayrollExportEnabled">
						<span class="ml-5">${messages["integrations.bmd.payrollExportEnabled"]}</span>
					</label>
					<div id="bmdPayrollExportSection" class="mb-20 panel panel-bordered p-20" style="display: none;">
						<div class="flex-row">
							<div class="flex-column-40 pr-20">
								<div><strong>${messages["integrations.bmd.homeOfficeAllowanceWageType"]}</strong></div>
								<div class="text-muted mt-5">${messages["integrations.bmd.homeOfficeAllowanceWageType.description"]}</div>
							</div>
							<div class="flex-column-60 form-group">
								<input type="text" class="form-control" id="bmdHomeOfficeAllowanceWageType" name="homeOfficeAllowanceWageType"/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</form>
	</div>
`;

export class BmdConfigDialog extends HTMLElement {
	private static readonly GREY_TEXT_COLOR_CLASS = "text-grey-300";
	private static readonly GUTSTD_DIV_NLZ = "GUTSTD_DIV_NLZ";

	private dom: JQuery;
	private modal: BootstrapModal.Modal;
	private editMode: boolean;
	private editIntegration: Integration;

	public static create() {
		const dialog = document.createElement("bmd-config-dialog") as BmdConfigDialog;
		$("body").append(dialog);
		dialog.create();
		dialog.remove();
	}

	public static edit(integration: Integration, configuration: BmdConfig) {
		const dialog = document.createElement("bmd-config-dialog") as BmdConfigDialog;
		$("body").append(dialog);
		dialog.edit(integration, configuration);
		dialog.remove();
	}

	private connectedCallback() {
		this.innerHTML = template;

		this.dom = $("#bmdConfigDialog");
		this.modal = new BootstrapModal.Modal({
			title: `<img src="/timr/images/integrations/bmd.svg" width="100px">`,
			escapeText: false,
			message: this.dom,
			size: "modal-lg",
			destroyOnClose: true,
			buttons: [
				{
					text: "dialog.save",
					css: "saveButton btn-primary",
					action: () => {
						if (!this.validateForm($("#bmdConfigForm"))) {
							this.modal.resetLoadingButtons();
							return;
						}
						if (!this.editMode) {
							Integrations.triggerAddedIntegrationEvent(IntegrationType.BMD, this.convertFormToJSONString($("#bmdConfigForm")));
						} else {
							Integrations.triggerEditedIntegrationEvent(IntegrationType.BMD, this.editIntegration, this.convertFormToJSONString($("#bmdConfigForm")));
						}
					}
				},
				{
					text: "dialog.cancel",
					css: "cancelButton btn-default"
				},
				{
					text: "dialog.delete",
					css: "deleteButton btn-danger",
					left: true,
					action: (m) => {
						BootstrapModal.confirm({
							title: messages["dialog.delete.confirm"],
							message: messages["integrations.bmd.delete.message"],
							yesAction: (confirmModal) => {
								confirmModal.close();
								m.close();
								Integrations.triggerDeletedIntegrationEvent(IntegrationType.BMD, this.editIntegration.id);
							}
						});
					}
				}
			],
			onOpen: (m) => {
				Input.toggle(m.find(".deleteButton"), this.editMode);
				this.dom.find("#bmdCompanyId").focus();
				Integrations.onSaveIntegration(IntegrationType.BMD, (e, fieldErrors: FieldError[]) => {
					if (!fieldErrors) {
						m.close();
					} else {
						FormUtil.markFieldErrors($("#bmdConfigForm"), fieldErrors);
						m.resetLoadingButtons();
					}
				});
			}
		});
	}

	private create() {
		this.editMode = false;
		this.applyConfig();
		void this.loadConfigurationData();
		this.modal.show();
	}

	private edit(editIntegration: Integration, config: BmdConfig) {
		this.editIntegration = editIntegration;
		this.editMode = true;
		this.applyConfig(config);
		void this.loadConfigurationData(config);
		this.modal.show();
	}

	private applyConfig(config?: BmdConfig) {
		this.dom.find("#bmdCompanyId").val(config?.companyId ?? "");
		this.dom.find("#bmdTimeAccountAbsenceExportEnabled").prop("checked", config?.timeAccountAbsenceExportEnabled);
		this.dom.find("#bmdTimeAccountBalanceExportEnabled").prop("checked", config?.timeAccountBalanceExportEnabled ?? true);
		this.dom.find("#bmdTimeAccountPayrollExportEnabled").prop("checked", config?.timeAccountPayrollExportEnabled);
		this.dom.find("#bmdHomeOfficeAllowanceWageType").val(config?.homeOfficeAllowanceWageType ?? "");
		this.toggleConfigSections();
	}

	private async loadConfigurationData(config?: BmdConfig) {
		try {
			const [dayBasedWorkingTimeTypes, bmdAbsenceTypes] = await Promise.all([
				api("integrations/bmd/dayBasedWorkingTimeTypes.json").get(),
				api("integrations/bmd/absenceTypes.json").get(),
			]);
			this.renderWorkingTimeTypeRows(dayBasedWorkingTimeTypes, bmdAbsenceTypes, config?.absenceTypes ?? {}, config?.divNlzIds ?? {});
		} catch {
			// error already surfaced by api()
		}
	}

	private renderWorkingTimeTypeRows(dayBasedWorkingTimeTypes: DayBasedWorkingTimeTypeDetails[], bmdAbsenceTypes: BmdAbsenceTypeDetails[], configuredAbsenceTypes: BmdAbsenceTypeConfig, configuredDivNlzIds: BmdDivNlzIdConfig) {
		const workingTimeTypeContainer = this.dom.find("#workingTimeTypeContainer");
		workingTimeTypeContainer.empty();

		dayBasedWorkingTimeTypes.forEach((workingTimeType) => {
			const row = $(this.buildWorkingTimeTypeRow(workingTimeType, configuredAbsenceTypes, configuredDivNlzIds, bmdAbsenceTypes));
			workingTimeTypeContainer.append(row);
			this.toggleDivNlzId(row);
		});
		this.initListeners();
		this.initDivNlzIdInfoPopover();
	}

	private buildWorkingTimeTypeRow(workingTimeType: DayBasedWorkingTimeTypeDetails, selectedAbsenceTypes: BmdAbsenceTypeConfig, selectedDivNlzIds: BmdDivNlzIdConfig, availableAbsenceTypes: BmdAbsenceTypeDetails[]) {
		const workingTimeTypeKey = String(workingTimeType.id);
		// mappings to absence types that are no longer selectable are shown as not exported
		const workingTimeTypeExport = availableAbsenceTypes.some((absenceType) => absenceType.key === selectedAbsenceTypes[workingTimeTypeKey]);
		const selectedAbsenceType = workingTimeTypeExport ? selectedAbsenceTypes[workingTimeTypeKey] : availableAbsenceTypes[0]?.key;
		const options = availableAbsenceTypes
			.map((absenceTypeOption) =>
				`<option value="${absenceTypeOption.key}" ${selectedAbsenceType === absenceTypeOption.key ? "selected" : ""}>${absenceTypeOption.number} - ${absenceTypeOption.name}</option>`)
			.join("");

		const divNlzId = selectedDivNlzIds[workingTimeTypeKey] ?? "";

		return `
			<div class="flex-row mb-10" data-working-time-type-row>
				<div class="flex-column-30 pt-8 text-nowrap overflow-ellipsis">${htmlEscape(workingTimeType.name)}</div>
				<div class="flex-column-10 mr-15 pt-8">
					<input data-working-time-type-export="true" type="checkbox" value="${workingTimeType.id}" ${workingTimeTypeExport ? "checked" : ""}>
				</div>
				<div class="flex-column-60">
					<div class="flex-row">
						<div class="flex-column-auto">
							<select class="form-control ${!workingTimeTypeExport ? BmdConfigDialog.GREY_TEXT_COLOR_CLASS : ""}" ${!workingTimeTypeExport ? "disabled" : ""}>
								${options}
							</select>
						</div>
						<div class="form-group mb-0 ml-10 width-150" data-div-nlz-id>
							<div class="input-group">
								<input type="text" inputmode="numeric" class="form-control" name="divNlzId-${workingTimeType.id}" data-working-time-type-id="${workingTimeType.id}"
									   placeholder="${messages["integrations.bmd.divNlzId"]}" value="${divNlzId}" ${!workingTimeTypeExport ? "disabled" : ""}>
								<span class="input-group-addon" data-div-nlz-id-info><i class="fal fa-info-circle"></i></span>
							</div>
						</div>
					</div>
				</div>
			</div>
		`;
	}

	private initListeners() {
		this.dom.find("[data-working-time-type-export]").off("change").on("change", (e) => {
			const checkbox = $(e.target);
			const row = checkbox.closest("[data-working-time-type-row]");
			const select = row.find("select");
			select.prop("disabled", !checkbox.is(":checked"));
			select.toggleClass(BmdConfigDialog.GREY_TEXT_COLOR_CLASS, !checkbox.is(":checked"));
			row.find("[data-div-nlz-id] input").prop("disabled", !checkbox.is(":checked"));
		});

		this.dom.find("[data-div-nlz-id] input").off("input").on("input", (e) => {
			const divNlzId = $(e.target);
			divNlzId.val((divNlzId.val() as string).replace(/\D/g, ""));
		});

		this.dom.find("[data-working-time-type-row] select").off("change").on("change", (e) => {
			this.toggleDivNlzId($(e.target).closest("[data-working-time-type-row]"));
		});

		this.dom.find("#bmdTimeAccountAbsenceExportEnabled, #bmdTimeAccountPayrollExportEnabled").off("change").on("change", () => this.toggleConfigSections());
	}

	/**
	 * Shows the Diverse-NLZ number input only for rows mapped to Art 6 (Gutstd./Div. NLZ).
	 */
	private toggleDivNlzId(row: JQuery) {
		row.find("[data-div-nlz-id]").toggle(row.find("select").val() === BmdConfigDialog.GUTSTD_DIV_NLZ);
	}

	private initDivNlzIdInfoPopover() {
		this.dom.find("[data-div-nlz-id-info]").popover({
			trigger: "hover",
			container: "body",
			placement: "top",
			html: true,
			content: messages["integrations.bmd.divNlzId.info"]
		});
	}

	private toggleConfigSections() {
		this.dom.find("#bmdAbsenceExportSection").toggle(this.dom.find("#bmdTimeAccountAbsenceExportEnabled").is(":checked"));
		this.dom.find("#bmdPayrollExportSection").toggle(this.dom.find("#bmdTimeAccountPayrollExportEnabled").is(":checked"));
	}

	private validateForm(form: JQuery): boolean {
		const companyIdValue = form.find("#bmdCompanyId").val() as string;
		const absenceExportEnabled = form.find("#bmdTimeAccountAbsenceExportEnabled").is(":checked");
		const timeAccountBalanceExportEnabled = form.find("#bmdTimeAccountBalanceExportEnabled").is(":checked");
		const payrollExportEnabled = form.find("#bmdTimeAccountPayrollExportEnabled").is(":checked");
		const selectedAbsenceTypeCount = form.find("[data-working-time-type-export]:checked").length;
		const homeOfficeAllowanceWageTypeValue = ((form.find("#bmdHomeOfficeAllowanceWageType").val() as string) ?? "").trim();
		const fieldErrors: FieldError[] = [];

		if (!companyIdValue) {
			fieldErrors.push({
				field: "companyId",
				message: messages["integrations.bmd.companyNr.notNull"]
			});
		}

		if (absenceExportEnabled && !timeAccountBalanceExportEnabled && selectedAbsenceTypeCount === 0) {
			fieldErrors.push({
				field: "absenceTypes",
				message: messages["integrations.bmd.absenceTypes.notNull"]
			});
		}

		if (absenceExportEnabled) {
			this.findExportedDivNlzIdInputs(form).each((i, e) => {
				const divNlzId = $(e);
				// the input only accepts digits, an empty value is 0
				if (Number(divNlzId.val()) <= 0) {
					fieldErrors.push({
						field: divNlzId.attr("name"),
						message: messages["integrations.bmd.divNlzId.notNull"]
					});
				}
			});
		}

		if (payrollExportEnabled && !homeOfficeAllowanceWageTypeValue) {
			fieldErrors.push({
				field: "homeOfficeAllowanceWageType",
				message: messages["integrations.bmd.homeOfficeAllowanceWageType.notNull"]
			});
		}

		FormUtil.removeAllFeedback(form);
		if (fieldErrors.length > 0) {
			FormUtil.markFieldErrors(form, fieldErrors);
			return false;
		}

		return true;
	}

	private convertFormToJSONString(form: JQuery): string {
		const companyIdValue = form.find("#bmdCompanyId").val() as string;
		const absenceExportEnabled = form.find("#bmdTimeAccountAbsenceExportEnabled").is(":checked");
		const timeAccountBalanceExportEnabled = form.find("#bmdTimeAccountBalanceExportEnabled").is(":checked");
		const payrollExportEnabled = form.find("#bmdTimeAccountPayrollExportEnabled").is(":checked");
		const homeOfficeAllowanceWageTypeValue = ((form.find("#bmdHomeOfficeAllowanceWageType").val() as string) ?? "").trim();

		const absenceTypesForExport: BmdAbsenceTypeConfig = {};
		const divNlzIdsForExport: BmdDivNlzIdConfig = {};
		if (absenceExportEnabled) {
			form.find("[data-working-time-type-export]:checked").each((i, e) => {
				const workingTimeTypeId = Number($(e).val());
				absenceTypesForExport[String(workingTimeTypeId)] = $(e).closest("[data-working-time-type-row]").find("select").val() as string;
			});
			this.findExportedDivNlzIdInputs(form).each((i, e) => {
				divNlzIdsForExport[$(e).attr("data-working-time-type-id")] = Number($(e).val());
			});
		}

		return JSON.stringify({
			companyId: companyIdValue,
			absenceTypes: absenceTypesForExport,
			divNlzIds: divNlzIdsForExport,
			homeOfficeAllowanceWageType: payrollExportEnabled ? homeOfficeAllowanceWageTypeValue : null,
			timeAccountAbsenceExportEnabled: absenceExportEnabled,
			timeAccountBalanceExportEnabled,
			timeAccountPayrollExportEnabled: payrollExportEnabled,
		});
	}

	/**
	 * Diverse-NLZ number inputs of the rows that are exported and mapped to Art 6 (Gutstd./Div. NLZ).
	 */
	private findExportedDivNlzIdInputs(form: JQuery): JQuery {
		return form.find("[data-working-time-type-row]")
			.filter((i, row) => $(row).find("[data-working-time-type-export]").is(":checked")
				&& $(row).find("select").val() === BmdConfigDialog.GUTSTD_DIV_NLZ)
			.find("[data-div-nlz-id] input");
	}
}
